import React, { useState, useEffect, useCallback } from "react";
import { api } from "../services/apiConfig";
import "../styles/userSettings.css";
import PreferencesForm from "./common/PreferencesForm";
import { convertToUTCHour, getUserLocalTimezone } from "../utils/timezoneUtils";

const UserSettings = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [userVerified, setUserVerified] = useState(false);
  const [userStatus, setUserStatus] = useState("active");
  const [pauseUntil, setPauseUntil] = useState("");
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [unsubscribeReason, setUnsubscribeReason] = useState("");
  const [unsubscribeFeedback, setUnsubscribeFeedback] = useState("");

  // User preference states
  const [frequencyDay, setFrequencyDay] = useState(1);
  const [userTimezone, setUserTimezone] = useState(getUserLocalTimezone());
  const [preferredHour, setPreferredHour] = useState(8);
  const [codingLanguage, setCodingLanguage] = useState("python"); // Default to Python

  // Use useCallback for fetchUserSettings to avoid infinite render loops
  const fetchUserSettings = useCallback(async (userEmail) => {
    try {
      const response = await api.get(
        `/api/user-settings?email=${encodeURIComponent(userEmail)}`
      );

      if (response.data && response.data.settings) {
        const settings = response.data.settings;
        setFrequencyDay(settings.frequency_day || 1);
        setUserTimezone(settings.user_timezone || getUserLocalTimezone());
        // Load the user's local preferred hour, not the UTC hour
        setPreferredHour(settings.preferred_hour || 8);
        setCodingLanguage(settings.coding_language || "python");
        setUserVerified(settings.verified || false);
        setUserStatus(settings.user_status || "active");
        if (settings.pause_until) {
          setPauseUntil(new Date(settings.pause_until).toISOString().split('T')[0]);
        }
      }
    } catch (error) {
      showMessage("error", "Failed to load your settings. Please try again.");
      console.error("Error fetching user settings:", error);
    } finally {
      setIsLoadingUserData(false);
    }
  }, []);

  // Load user data on component mount
  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    if (userEmail) {
      setEmail(userEmail);
      fetchUserSettings(userEmail);
    } else {
      setIsLoadingUserData(false);
    }
  }, [fetchUserSettings]); // Add fetchUserSettings as a dependency

  // showMessage function definition
  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => {
      setMessage({ type: "", text: "" });
    }, 5000);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("frequency_day", frequencyDay);
      formData.append("user_timezone", userTimezone);
      formData.append("preferred_hour", preferredHour);
      formData.append(
        "preferred_utc_hour",
        convertToUTCHour(preferredHour, userTimezone)
      );
      formData.append("coding_language", codingLanguage);

      const response = await api.post("/api/update-settings", formData);

      if (response.data.message) {
        showMessage(
          "success",
          `${response.data.message}. Note: Your next PrepLetter schedule may NOT reflect the changes you have just made.`
        );
      }
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle pause emails
  const handlePauseEmails = async () => {
    if (!pauseUntil) {
      showMessage("error", "Please select a date to resume emails.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(`/api/user-settings/pause`, {
        email: email,
        pause_until: pauseUntil
      });

      if (response.data.message) {
        showMessage("success", response.data.message);
        setUserStatus("paused");
        setShowPauseModal(false);
      }
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message || "Failed to pause emails. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle unsubscribe
  const handleUnsubscribe = async () => {
    if (!unsubscribeReason) {
      showMessage("error", "Please select a reason for unsubscribing.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post(`/api/user-settings/unsubscribe`, {
        user_email: email,
        reason: unsubscribeReason,
        feedback: unsubscribeFeedback
      });

      if (response.data.message) {
        showMessage("success", response.data.message);
        setUserStatus("unsubscribed");
        setShowUnsubscribeModal(false);
        setUnsubscribeReason("");
        setUnsubscribeFeedback("");
      }
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message || "Failed to unsubscribe. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reactivate
  const handleReactivate = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(`/api/user-settings/reactivate`, {
        email: email
      });

      if (response.data.message) {
        showMessage("success", response.data.message);
        setUserStatus("active");
        setPauseUntil("");
      }
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message || "Failed to reactivate. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingUserData) {
    return (
      <div className="user-settings-container loading">
        <div className="loading-spinner"></div>
        <p>Loading your settings...</p>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="user-settings-container">
        <h2>User Settings</h2>
        <div className="settings-card">
          <p className="not-logged-in">
            Please log in to view and update your email preferences.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-settings-container">
      <h2>Email Preferences</h2>
      <div className="settings-card">
        <form onSubmit={handleSubmit}>
          <div className="settings-header">
            <div className="user-email-display">
              <span className="email-label">Your Email:</span>
              <span className="email-value">{email}</span>
              {userVerified && <span className="verified-badge">Verified</span>}
            </div>
          </div>

          <div className="settings-group">
            <PreferencesForm
              frequencyDay={frequencyDay}
              setFrequencyDay={setFrequencyDay}
              userTimezone={userTimezone}
              setUserTimezone={setUserTimezone}
              preferredHour={preferredHour}
              setPreferredHour={setPreferredHour}
              codingLanguage={codingLanguage}
              setCodingLanguage={setCodingLanguage}
              variant="settings"
            />
          </div>

          {message.text && (
            <div className={`alert ${message.type}`}>{message.text}</div>
          )}

          <div className="settings-actions">
            <button type="submit" className="save-button" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Preferences"}
            </button>
          </div>
        </form>

        {/* Email Management Section */}
        <div className="email-management-section">
          <h3>Email Management</h3>
          
          {/* Current Status Display */}
          <div className={`status-indicator ${userStatus === "100_completed" ? "completed" : userStatus}`}>
            <span className="status-text">
              {userStatus === "active" && "✅ Active - Receiving emails"}
              {userStatus === "paused" && `⏸️ Paused until ${pauseUntil ? new Date(pauseUntil).toLocaleDateString() : 'N/A'}`}
              {userStatus === "unsubscribed" && "❌ Unsubscribed"}
              {userStatus === "100_completed" && "🎉 Journey Complete!"}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="email-actions">
            {userStatus === "active" && (
              <>
                <button 
                  onClick={() => setShowPauseModal(true)} 
                  className="btn-modern btn-warning"
                >
                  ⏸️ Pause Emails
                </button>
                <button 
                  onClick={() => setShowUnsubscribeModal(true)} 
                  className="btn-modern btn-danger"
                >
                  ❌ Unsubscribe
                </button>
              </>
            )}
            
            {(userStatus === "paused" || userStatus === "unsubscribed") && (
              <button 
                onClick={handleReactivate} 
                className="btn-modern btn-success"
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : "✅ Reactivate Emails"}
              </button>
            )}
          </div>
        </div>

        {/* Pause Modal */}
        {showPauseModal && (
          <div className="quiz-modal">
            <div className="quiz-preparation-modal">
              <div className="quiz-modal-header">
                <h3>⏸️ Pause Your PrepLetter Emails</h3>
                <p>Need a break? You can pause your emails and resume them later.</p>
              </div>
              
              <div className="quiz-modal-body">
                <div className="pause-form">
                  <div className="form-group">
                    <label htmlFor="pauseUntil">Resume emails on:</label>
                    <input
                      type="date"
                      id="pauseUntil"
                      value={pauseUntil}
                      onChange={(e) => setPauseUntil(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="form-control"
                    />
                  </div>
                  <div className="pause-info">
                    <p><strong>What happens when you pause:</strong></p>
                    <ul>
                      <li>You'll stop receiving PrepLetter emails immediately</li>
                      <li>Your progress will be saved</li>
                      <li>Emails will automatically resume on your selected date</li>
                      <li>You can reactivate anytime before the resume date</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="quiz-modal-footer">
                <div className="d-flex justify-content-between">
                  <button 
                    onClick={() => {
                      setShowPauseModal(false);
                      setPauseUntil("");
                    }} 
                    className="btn-modern btn-secondary"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handlePauseEmails} 
                    className="btn-modern btn-warning"
                    disabled={isLoading || !pauseUntil}
                  >
                    {isLoading ? "Processing..." : "Pause Emails"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unsubscribe Modal */}
        {showUnsubscribeModal && (
          <div className="quiz-modal">
            <div className="quiz-preparation-modal">
              <div className="quiz-modal-header">
                <h3>❌ Unsubscribe from PrepLetter</h3>
                <p>We're sorry to see you go. Please help us improve by telling us why you're leaving.</p>
              </div>
              
              <div className="quiz-modal-body">
                <div className="unsubscribe-form">
                  <div className="form-group">
                    <label>What's your main reason for unsubscribing?</label>
                    <div className="reason-options">
                      {[
                        "Too many emails",
                        "Content not relevant",
                        "Found a better alternative", 
                        "Completed my learning goals",
                        "Taking a break from coding",
                        "Email delivery issues",
                        "Privacy concerns",
                        "Other"
                      ].map((reason) => (
                        <label key={reason} className="reason-option">
                          <input
                            type="radio"
                            name="unsubscribeReason"
                            value={reason}
                            checked={unsubscribeReason === reason}
                            onChange={(e) => setUnsubscribeReason(e.target.value)}
                          />
                          <span>{reason}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="unsubscribeFeedback">
                      Additional feedback (optional):
                    </label>
                    <textarea
                      id="unsubscribeFeedback"
                      value={unsubscribeFeedback}
                      onChange={(e) => setUnsubscribeFeedback(e.target.value)}
                      placeholder="Tell us more about your experience..."
                      rows="3"
                      className="form-control"
                    />
                  </div>

                  <div className="unsubscribe-warning">
                    <p><strong>⚠️ Before you go:</strong></p>
                    <ul>
                      <li>Consider pausing instead of unsubscribing</li>
                      <li>You can always resubscribe later</li>
                      <li>Your progress will be saved</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="quiz-modal-footer">
                <div className="d-flex justify-content-between">
                  <button 
                    onClick={() => {
                      setShowUnsubscribeModal(false);
                      setUnsubscribeReason("");
                      setUnsubscribeFeedback("");
                    }} 
                    className="btn-modern btn-secondary"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleUnsubscribe} 
                    className="btn-modern btn-danger"
                    disabled={isLoading || !unsubscribeReason}
                  >
                    {isLoading ? "Processing..." : "Confirm Unsubscribe"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSettings;
